// =============================================
// مكون المميزات - DATX
// تصميم مميز وجذاب مع ألوان متسقة وهندسة بصرية فاخرة
// =============================================

import { motion } from 'framer-motion';
import { 
  Laptop, 
  GraduationCap, 
  Award, 
  TrendingUp,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Features = () => {
  // =============================================
  // بيانات المميزات
  // =============================================
  const features = [
    {
      icon: <Laptop className="w-6 h-6" strokeWidth={1.75} />,
      title: "تعلم عن بعد",
      description: "دورات تفاعلية عبر الإنترنت مع مدربين محترفين ومشاريع حقيقية",
      gradient: "from-blue-500/10 to-indigo-500/5",
      badgeColor: "bg-blue-500/10 text-blue-600"
    },
    {
      icon: <GraduationCap className="w-6 h-6" strokeWidth={1.75} />,
      title: "خبراء متخصصون",
      description: "تعلم على يد نخبة من الخبراء والأكاديميين في مجال علوم البيانات",
      gradient: "from-indigo-500/10 to-purple-500/5",
      badgeColor: "bg-indigo-500/10 text-indigo-600"
    },
    {
      icon: <Award className="w-6 h-6" strokeWidth={1.75} />,
      title: "شهادات معتمدة",
      description: "احصل على شهادات معتمدة تسهم في تسريع قبولك بسوق العمل",
      gradient: "from-purple-500/10 to-pink-500/5",
      badgeColor: "bg-purple-500/10 text-purple-600"
    },
    {
      icon: <TrendingUp className="w-6 h-6" strokeWidth={1.75} />,
      title: "تطور مستمر",
      description: "مناهج تعليمية متجددة تواكب أحدث التقنيات وأدوات تحليل البيانات",
      gradient: "from-cyan-500/10 to-blue-500/5",
      badgeColor: "bg-cyan-500/10 text-cyan-600"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-slate-50/50 to-white relative overflow-hidden">
      {/* =============================================
          خلفية زخرفية
          ============================================= */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/3 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* =============================================
            عنوان القسم
            ============================================= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#020B14] mb-4 tracking-tight">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              لماذا
            </span>
            <span className="text-[#020B14]"> تختار داتكس؟</span>
          </h2>
          
          {/* خط زخرفي */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-0.5 bg-gradient-to-r from-transparent to-blue-600/30" />
            <div className="w-2.5 h-2.5 rounded-full bg-blue-600 rotate-45" />
            <div className="w-12 h-0.5 bg-gradient-to-l from-transparent to-blue-600/30" />
          </div>
          
          <p className="text-lg max-w-2xl mx-auto font-normal leading-relaxed" style={{ color: '#282828' }}>
            نقدم لك بيئة تعليمية ذكية تجمع بين المفاهيم النظرية العميقة والتطبيقات العملية والمهنية الأكثر طلباً في سوق العمل.
          </p>
        </motion.div>

        {/* =============================================
            شبكة المميزات
            ============================================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative"
            >
              {/* خلفية البطاقة الفاخرة */}
              <div className="absolute inset-0 bg-white rounded-2xl shadow-md group-hover:shadow-xl transition-all duration-500 border border-slate-100" />
              
              {/* تدرج خفيف عند التمرير */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              
              <div className="relative p-8 rounded-2xl transition-all duration-500">
                {/* رقم البطاقة الجمالي الواضح */}
                <div className="absolute top-4 left-4 text-4xl sm:text-5xl font-black text-slate-300 group-hover:text-blue-600/30 transition-colors duration-500 select-none tracking-tighter">
                  {String(index + 1).padStart(2, '0')}
                </div>

                {/* الأيقونة المضيئة */}
                <div className="relative mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all duration-500 group-hover:scale-105 group-hover:rotate-3 shadow-inner">
                    {feature.icon}
                  </div>
                  <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-blue-500/10 group-hover:bg-blue-500/20 transition-all duration-500 animate-pulse" />
                </div>

                {/* المحتوى */}
                <h3 className="text-xl font-bold text-[#020B14] mb-3 group-hover:text-blue-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed group-hover:text-slate-600 transition-colors">
                  {feature.description}
                </p>

                {/* خط مؤشر سفلي */}
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right rounded-b-2xl" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* =============================================
            زر دعوة إضافي
            ============================================= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          <Link
            to="/courses"
            className="inline-flex items-center gap-3 px-8 py-3.5 bg-[#020B14] hover:bg-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:-translate-y-0.5 group"
          >
            <span>ابدأ التعلم الآن</span>
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;